"use client";

import { useEffect } from "react";
import type { ReactElement } from "react";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { LazyLoadAfterTrigger } from "@/src/component/partial/LazyLoadAfterTrigger";
import CMSLogic from "@/src/logic/CMSLogic";
import { StrapiType } from "@/src/type";
import { LoadFromCDN } from "@/src/util";

interface VideoPlayerProps {
  asset?: StrapiType.StrapiAsset;
  url?: string;
  videoId?: string;
  classes?: {
    container?: string;
    video?: string;
    previewPoster?: string;
  };
  poster?: string;
  lazy: "eager" | "lazy";
  muted?: boolean;
  play?: boolean;
  AfterVideoComponent?: ReactElement;
  triggers?: string[];
  updateOperation?: "begin" | "pause" | undefined;
  controls?: boolean;
  dontLoadUntilEager?: boolean;
  resolution?: "low" | "medium" | "high";
}

function VideoPlayer({
  lazy,
  asset,
  url,
  videoId,
  classes,
  poster,
  muted = true,
  play = true,
  AfterVideoComponent,
  triggers = ["swiperSlideChange", "click", "touchstart", "scroll"],
  updateOperation,
  controls,
  dontLoadUntilEager,
}: VideoPlayerProps) {
  /**
   * this makes sure the video always starts
   */
  useEffect(() => {
    if (!CMSLogic.isVideo(asset) && !url) {
      return;
    }

    if (!play) {
      return;
    }

    const interval = setInterval(() => {
      const video: any = document.getElementById(videoId ?? "");
      if (video) {
        try {
          video.play();
          //video.volume = 1.0;
        } catch (err) {}
        clearInterval(interval);
      }
    }, 150);

    return () => clearInterval(interval);
  }, [videoId, asset, url, play]);

  /**
   * this works to restart the video
   * in order to auto turn on sound, video must be playing then a click can turn on sound
   */
  useEffect(() => {
    if (!updateOperation) {
      return;
    }

    if (updateOperation === "begin") {
      const video: any = document.getElementById(videoId ?? "");
      if (video) {
        try {
          video.currentTime = 0;
          video.play();
        } catch (err) {}
      }
    } else if (updateOperation === "pause") {
      const video: any = document.getElementById(videoId ?? "");
      if (video) {
        try {
          video.pause();
        } catch (err) {}
      }
    }
  }, [updateOperation, videoId]);

  if (CMSLogic.isVideo(asset) || typeof url === "string") {
    return (
      <LazyLoadAfterTrigger
        lazy={lazy}
        triggers={triggers}
        autoTrigger={true}
        dontLoadUntilEager={dontLoadUntilEager}
        ComponentLazyLoad={
          poster ? (
            <ImgBgResponsive
              imageUrl={poster ? LoadFromCDN.nextGenFormat(poster) : ""}
              className={classes?.previewPoster}
            />
          ) : (
            <></>
          )
        }
      >
        <div
          className={classes?.container}
          id={`container.${videoId}`}
        >
          <video
            id={videoId}
            loop={true}
            muted={muted}
            data-autoplay={play}
            playsInline
            preload="auto"
            autoPlay={play}
            className={classes?.video}
            src={asset?.data?.attributes?.url ?? url}
            poster={poster ? LoadFromCDN.nextGenFormat(poster) : undefined}
            controls={controls}
          >
            <source
              src={asset?.data?.attributes?.url ?? url}
              type={asset?.data?.attributes?.mime ?? "video/mp4"}
            />
          </video>
          {AfterVideoComponent}
        </div>
      </LazyLoadAfterTrigger>
    );
  }
  return <></>;
}

export default VideoPlayer;
